import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Sparkles, Edit3 } from 'lucide-react';
import TiptapEditor from '../Editor/TiptapEditor';
import useUserStore from '../../stores/useUserStore';
import { isTiptapJson } from '../../utils/migrateMd';
import './DualModeEditor.css';

/**
 * Extracts plain text from Tiptap JSON or Markdown string
 */
export function getPlainText(content) {
    if (!content) return '';
    if (typeof content === 'string') {
        if (isTiptapJson(content)) {
            try {
                return getPlainText(JSON.parse(content));
            } catch {
                return content;
            }
        }
        return content;
    }
    
    // Tiptap JSON node
    if (content.type === 'text') {
        return content.text || '';
    }
    
    if (content.content && Array.isArray(content.content)) {
        // Blocks like paragraphs or headings usually need a newline
        const isBlock = ['paragraph', 'heading', 'listItem', 'codeBlock'].includes(content.type);
        const childText = content.content.map(getPlainText).join('');
        return childText + (isBlock ? '\n\n' : '');
    }
    
    return '';
}

/**
 * DualModeEditor
 * Wraps an auto-resizing textarea (Simple Mode) and TiptapEditor (Advanced Mode).
 * Controlled globally by `useUserStore.preferences.editorMode`.
 */
export default function DualModeEditor({
    content,
    onUpdate,
    editable = true,
    placeholder = 'Start writing...',
    className = '',
    compact = false,
    forceAdvanced = false
}) {
    const preferences = useUserStore(s => s.preferences);
    const updatePreferences = useUserStore(s => s.updatePreferences);
    const editorMode = forceAdvanced ? 'advanced' : (preferences.editorMode || 'simple');
    
    // For simple mode, we extract plain text to show in the textarea
    const [simpleText, setSimpleText] = useState(() => getPlainText(content).replace(/\n+$/, ''));
    const textareaRef = useRef(null);
    const isFocusedRef = useRef(false);

    // Sync external content changes into simple text if we're in simple mode
    // Skip resync when the user is actively typing (focused) to avoid stripping trailing spaces
    useEffect(() => {
        if (editorMode === 'simple' && !isFocusedRef.current) {
            setSimpleText(getPlainText(content).replace(/\n+$/, ''));
        }
    }, [content, editorMode]);

    // Auto-resize simple textarea
    useEffect(() => {
        if (editorMode === 'simple' && textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.max(textareaRef.current.scrollHeight, 60)}px`;
        }
    }, [simpleText, editorMode]);

    const handleSimpleChange = (e) => {
        const newText = e.target.value;
        setSimpleText(newText);
        
        // When typing in simple mode, emit standard Tiptap JSON so the data structure remains consistent.
        // Single paragraph with all the text.
        const jsonContent = {
            type: 'doc',
            content: [
                {
                    type: 'paragraph',
                    content: newText ? [{ type: 'text', text: newText }] : []
                }
            ]
        };
        onUpdate?.(jsonContent);
    };

    const toggleMode = () => {
        const newMode = editorMode === 'simple' ? 'advanced' : 'simple';
        updatePreferences({ editorMode: newMode });
    };

    return (
        <div className={`dual-mode-editor ${className}`}>
            {editorMode === 'advanced' ? (
                <div className="dual-mode-editor__advanced">
                    <TiptapEditor
                        content={content}
                        onUpdate={onUpdate}
                        editable={editable}
                        placeholder={placeholder}
                        compact={compact}
                    />
                </div>
            ) : (
                <div className="dual-mode-editor__simple">
                    <textarea
                        ref={textareaRef}
                        className={`dual-mode-editor__textarea ${!editable ? 'dual-mode-editor__textarea--readonly' : ''}`}
                        value={simpleText}
                        onChange={handleSimpleChange}
                        onFocus={() => { isFocusedRef.current = true; }}
                        onBlur={() => { isFocusedRef.current = false; }}
                        placeholder={placeholder}
                        readOnly={!editable}
                        rows={1}
                    />
                </div>
            )}

            {editable && !forceAdvanced && !compact && (
                <div className="dual-mode-editor__footer">
                    <button 
                        type="button" 
                        className="dual-mode-editor__toggle" 
                        onClick={toggleMode}
                    >
                        {editorMode === 'simple' ? (
                            <><Sparkles size={12} /> Switch to Advanced Editor</>
                        ) : (
                            <><Edit3 size={12} /> Switch to Simple Editor</>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}
